import { Button } from '@mantine/core';
import { useInterval, useWindowEvent } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconReload } from '@tabler/icons-react';
import { useCallback } from 'react';

const NEW_UPDATE_NOTIFICATION_ID = 'new-update';
const UPDATE_DISMISSED_KEY = 'update-notification-dismissed';
const DISMISS_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

/**
 * Check to see if front-end and backend are in sync, based on build-id
 * Refetches every 5 minutes and when user returns to the tab
 */
export const useCheckForUpdates = () => {
  const fetchAndCheckBuildId = useCallback(async () => {
    // Skip if user dismissed the notification within the last 4 hours
    const dismissedAt = sessionStorage.getItem(UPDATE_DISMISSED_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DURATION_MS) {
      return;
    }

    try {
      // This is the build-id that we set in the app.yaml process (should be equal to vercel build id)
      const response = await fetch('/build-id.txt', {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Response not ok: ${response.status}`);
      }

      const text = await response.text();
      const backendBuildId = text.trim();

      // Skip for development
      if (backendBuildId === 'dev-build-id') {
        return;
      }

      const frontendBuildId = process.env.NEXT_PUBLIC_GIT_SHA;

      if (!backendBuildId || !frontendBuildId) {
        throw new Error(`Build ID is not set. backendBuildId: ${backendBuildId}, frontendBuildId: ${frontendBuildId}`);
      }

      if (frontendBuildId !== backendBuildId) {
        notifications.show({
          id: NEW_UPDATE_NOTIFICATION_ID,
          withCloseButton: false,
          autoClose: false,
          title: 'New Update Available',
          color: 'blue',
          position: 'bottom-right',
          icon: <IconReload />,
          message: (
            <div className="flex flex-col gap-2">
              <div className="text-xs">We&apos;ve made some improvements. Reload to get the latest version.</div>
              <div className="flex gap-2">
                <Button
                  size="xs"
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => {
                    sessionStorage.setItem(UPDATE_DISMISSED_KEY, String(Date.now()));
                    notifications.hide(NEW_UPDATE_NOTIFICATION_ID);
                  }}
                >
                  Later
                </Button>
              </div>
            </div>
          ),
        });
      }
    } catch (err) {
      console.warn('Error checking build ID:', err);
    }
  }, []);

  // check for updates on tab changes
  useWindowEvent('visibilitychange', () => {
    if (!document.hidden) {
      void fetchAndCheckBuildId();
    }
  });

  // check for updates every 5 minutes
  useInterval(fetchAndCheckBuildId, 5 * 60 * 1000, { autoInvoke: true });
};
